import React, {useEffect, useState} from 'react';
import {App, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip} from 'antd';
import {
    CheckCircleOutlined,
    CheckOutlined,
    DeleteOutlined,
    EditOutlined,
    KeyOutlined,
    LogoutOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    UserOutlined
} from '@ant-design/icons';
import {User, userManagementService} from '../../services/userManagementService';
import {Role, roleService} from '../../services/roleService';
import {useAuth} from '../../contexts/AuthContext';

const {Option} = Select;

const UserManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const {refreshUser} = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [form] = Form.useForm();
    const [detailForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    useEffect(() => {
        loadUsers();
        loadRoles();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const data = await userManagementService.getUserList();
            setUsers(data);
        } catch (error) {
            messageApi.error('获取用户列表失败');
        } finally {
            setLoading(false);
        }
    };

    const loadRoles = async () => {
        try {
            const data = await roleService.getAllRoles();
            setRoles(data);
        } catch (error) {
            messageApi.error('获取角色列表失败');
        }
    };

    const handleCreate = () => {
        setEditingUser(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (user: User) => {
        setEditingUser(user);
        form.setFieldsValue({
            username: user.username,
            email: user.email,
            roleIds: user.roles?.map(r => r.roleId),
        });
        setModalVisible(true);
    };

    const handleChangePassword = (user: User) => {
        setSelectedUser(user);
        passwordForm.resetFields();
        setPasswordModalVisible(true);
    };

    const handlePasswordSubmit = async () => {
        try {
            const values = await passwordForm.validateFields();
            await userManagementService.resetPassword(selectedUser!.userId!, values.newPassword);
            messageApi.success('密码修改成功');
            setPasswordModalVisible(false);
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const handleViewDetail = async (user: User) => {
        try {
            const detail = await userManagementService.getUserDetail(user.userId!);
            setSelectedUser(detail);
            detailForm.setFieldsValue({
                username: detail.username,
                email: detail.email,
                status: detail.status,
                roleIds: detail.roles?.map(r => r.roleId),
            });
            setDetailModalVisible(true);
        } catch (error) {
            messageApi.error('获取用户详情失败');
        }
    };

    const handleDelete = async (userId: number) => {
        try {
            await userManagementService.setUserStatus(userId, false);
            messageApi.success('禁用用户成功');
            loadUsers();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const handleEnable = async (userId: number) => {
        try {
            await userManagementService.setUserStatus(userId, true);
            messageApi.success('启用用户成功');
            loadUsers();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const handleForceLogout = async (userId: number) => {
        try {
            await userManagementService.forceLogout(userId);
            messageApi.success('强制下线成功');
            loadUsers();
        } catch (error: any) {
            messageApi.error(error.message || '强制下线失败');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingUser) {
                await userManagementService.updateUser(editingUser.userId!, values);
                messageApi.success('更新成功');
            } else {
                await userManagementService.createUser(values);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadUsers();
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const handleAssignRoles = async () => {
        try {
            const values = await detailForm.validateFields();
            await userManagementService.assignRoles(selectedUser!.userId!, values.roleIds);
            messageApi.success('角色分配成功');
            setDetailModalVisible(false);
            loadUsers();
            refreshUser();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return <Tag color="green">正常</Tag>;
            case 'INACTIVE':
                return <Tag color="red">禁用</Tag>;
            case 'LOCKED':
                return <Tag color="orange">锁定</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    // 时间格式化
    const formatDateTime = (value: string) => {
        if (!value) return '-';
        return value.replace('T', ' ').substring(0, 19);
    };

    // 格式化权限 accessMode: 1=R, 2=W, 4=E
    const formatAccessMode = (accessMode: number) => {
        const flags = [];
        if (accessMode & 1) flags.push('R');
        if (accessMode & 2) flags.push('W');
        if (accessMode & 4) flags.push('E');
        return flags.join('/');
    };

    const columns = [
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
            render: (username: string, record: User) => (
                <Space>
                    <span>{username}</span>
                    <Tooltip title={record.isOnline ? '在线' : '离线'}>
                        {record.isOnline
                            ? <CheckCircleOutlined style={{color: 'var(--pt-bull)'}}/>
                            : <MinusCircleOutlined style={{color: 'var(--pt-text-secondary)'}}/>
                        }
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => getStatusTag(status),
        },
        {
            title: '角色',
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: Role[]) => (
                <Space>
                    {roles?.map(role => (
                        <Tag key={role.roleId} color="blue">{role.roleName}</Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 240,
            render: (_: any, record: User) => (
                <Space size={4}>
                    <Tooltip title="详情">
                        <Button type="text" icon={<UserOutlined/>} onClick={() => handleViewDetail(record)}
                                size="small"/>
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button type="text" icon={<EditOutlined/>} onClick={() => handleEdit(record)} size="small"/>
                    </Tooltip>
                    <Tooltip title="修改密码">
                        <Button type="text" icon={<KeyOutlined/>} onClick={() => handleChangePassword(record)}
                                size="small"/>
                    </Tooltip>
                    {record.status === 'ACTIVE' ? (
                        <Tooltip title="禁用">
                            <Popconfirm title="确定禁用该用户吗？" onConfirm={() => handleDelete(record.userId!)}>
                                <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                            </Popconfirm>
                        </Tooltip>
                    ) : (
                        <Tooltip title="启用">
                            <Button type="text" icon={<CheckOutlined/>} onClick={() => handleEnable(record.userId!)}
                                    size="small"/>
                        </Tooltip>
                    )}
                    {record.isOnline && (
                        <Tooltip title="强制下线">
                            <Popconfirm
                                title="确定强制该用户下线吗？"
                                description="该用户将被断开所有会话"
                                onConfirm={() => handleForceLogout(record.userId!)}
                            >
                                <Button type="text" danger icon={<LogoutOutlined/>} size="small"/>
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{marginBottom: 16, textAlign: 'right'}}>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={users}
                rowKey="userId"
                loading={loading}
            />

            <Modal
                title={editingUser ? '编辑用户' : '创建用户'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="用户名" rules={[{required: true, message: '请输入用户名'}]}>
                        <Input disabled={!!editingUser}/>
                    </Form.Item>
                    {!editingUser && (
                        <Form.Item name="password" label="密码" rules={[{required: true, message: '请输入密码'}]}>
                            <Input.Password/>
                        </Form.Item>
                    )}
                    <Form.Item name="email" label="邮箱">
                        <Input/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="用户详情"
                open={detailModalVisible}
                onOk={handleAssignRoles}
                onCancel={() => setDetailModalVisible(false)}
                width={600}
            >
                <Form form={detailForm} layout="vertical">
                    <Form.Item name="username" label="用户名">
                        <Input disabled/>
                    </Form.Item>
                    <Form.Item name="email" label="邮箱">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="status" label="状态">
                        <Input disabled/>
                    </Form.Item>
                    <Form.Item name="roleIds" label="分配角色">
                        <Select mode="multiple" placeholder="选择角色">
                            {roles.map(role => (
                                <Option key={role.roleId} value={role.roleId!}>
                                    {role.roleName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {selectedUser?.permissions && (
                        <div style={{marginTop: 16}}>
                            <h4>用户权限（合并后）</h4>
                            <div style={{maxHeight: 200, overflow: 'auto'}}>
                                {selectedUser.permissions.map(p => (
                                    <Tag key={p.permissionId} style={{marginBottom: 4}}>
                                        {p.permissionName}({formatAccessMode(p.accessMode || 0)})
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                </Form>
            </Modal>

            {/* 修改密码弹窗 */}
            <Modal
                title="修改密码"
                open={passwordModalVisible}
                onOk={handlePasswordSubmit}
                onCancel={() => setPasswordModalVisible(false)}
            >
                <Form form={passwordForm} layout="vertical">
                    <Form.Item name="newPassword" label="新密码" rules={[{required: true, message: '请输入新密码'}]}>
                        <Input.Password/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagement;